import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { getBackendUrl } from "@/utils/api";
import ToolPageHeader from '../components/ToolPageHeader';
import { useLanguage } from '../contexts/LanguageContext';

// SEO Configuration for AI Search Engines
const SEO_CONFIG = {
  title: 'Radio Campaign Analyzer | GRP Calculator & Media Planning - Radio Solutions',
  description: 'Plan and analyze your radio advertising campaigns. Calculate GRPs, reach, frequency and optimize your media budget with our advanced radio campaign planning tool.',
  keywords: 'radio campaign analyzer, GRP calculator, radio advertising planner, media planning tool, αναλυτής καμπάνιας ραδιοφώνου, υπολογισμός GRP, radio media buying, reach frequency calculator, radio ad effectiveness, campaign optimization, διαφημιστική καμπάνια ραδιόφωνο, radio ROI calculator',
  ogUrl: 'https://radio-apps.online/campaign-calculator'
};

const CampaignCalculator = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    brandCategory: '',
    marketPosition: '',
    radioPrimary: '',
    campaignDuration: ''
  });

  const [results, setResults] = useState(null);
  
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Daily spots per week (7 days)
  const [dailySpots, setDailySpots] = useState({
    monday: 5,
    tuesday: 6,
    wednesday: 7,
    thursday: 8,
    friday: 9,
    saturday: 10,
    sunday: 5
  });

  // Translated weekdays
  const weekDays = [
    { key: 'monday', labelKey: 'monday' },
    { key: 'tuesday', labelKey: 'tuesday' },
    { key: 'wednesday', labelKey: 'wednesday' },
    { key: 'thursday', labelKey: 'thursday' },
    { key: 'friday', labelKey: 'friday' },
    { key: 'saturday', labelKey: 'saturday' },
    { key: 'sunday', labelKey: 'sunday' }
  ];

  // Translated brand categories
  const brandCategories = [
    { value: 'τρόφιμα', labelKey: 'foodDrinks' },
    { value: 'τεχνολογία', labelKey: 'technology' },
    { value: 'μόδα', labelKey: 'fashionBeauty' },
    { value: 'αυτοκίνητα', labelKey: 'automobiles' },
    { value: 'υπηρεσίες', labelKey: 'services' },
    { value: 'λιανική', labelKey: 'retail' }
  ];

  // Translated market positions
  const marketPositions = [
    { value: 'leader', labelKey: 'marketLeader' },
    { value: 'challenger', labelKey: 'challenger' },
    { value: 'new', labelKey: 'newEntry' }
  ];

  // Translated radio options
  const radioPrimaryOptions = [
    { value: 'yes', labelKey: 'yes' },
    { value: 'no', labelKey: 'no' }
  ];

  // Translated campaign durations
  const campaignDurations = [
    { value: '2', labelKey: 'weeks2' },
    { value: '4', labelKey: 'weeks4' },
    { value: '8', labelKey: 'weeks8' },
    { value: '12', labelKey: 'weeks12' }
  ];

  const calculateCampaign = () => {
    const { brandCategory, marketPosition, radioPrimary, campaignDuration } = formData;
    
    if (!brandCategory || !marketPosition || !radioPrimary || !campaignDuration) {
      toast.error('⚠️ ' + t('fillAllFields'));
      return;
    }

    const weeks = parseInt(campaignDuration);
    
    // Calculate spots per week and total
    const weeklySpots = Object.values(dailySpots).reduce((sum, spots) => sum + parseInt(spots || 0), 0);
    const totalSpots = weeklySpots * weeks;
    
    // Base reach calculation (based on spots and market factors)
    let baseReach = 25; // Starting reach
    
    // Market position affects reach
    if (marketPosition === 'leader') baseReach += 10;
    else if (marketPosition === 'challenger') baseReach += 5;
    else if (marketPosition === 'new') baseReach += 0;
    
    // Radio primary affects reach
    if (radioPrimary === 'yes') baseReach += 5;
    
    // Spots increase reach (diminishing returns)
    const spotReachBonus = Math.min(30, Math.log(weeklySpots) * 8);
    const estimatedReach = Math.min(85, Math.round(baseReach + spotReachBonus));
    
    // Frequency = Total Spots / (Reach% × Population)
    // Simplified: Frequency increases with spots
    const estimatedFrequency = (totalSpots / (estimatedReach * weeks)).toFixed(1);
    
    // GRPs = Reach × Frequency
    const totalGRPs = Math.round(estimatedReach * parseFloat(estimatedFrequency));
    const weeklyGRPs = Math.round(totalGRPs / weeks);

    // Generate weekly breakdown with daily spots
    const weeklyData = [];
    for (let i = 1; i <= weeks; i++) {
      const weekReach = Math.min(estimatedReach, Math.round(estimatedReach * (i / weeks)));
      weeklyData.push({
        week: `${t('weekLabel')} ${i}`,
        spots: weeklySpots,
        GRPs: weeklyGRPs,
        Reach: weekReach,
        frequency: (weeklyGRPs / weekReach).toFixed(1)
      });
    }
    
    // Daily breakdown for Week 1 (example)
    const dailyBreakdown = weekDays.map(day => ({
      day: t(day.labelKey),
      spots: dailySpots[day.key] || 0
    }));
    
    // Performance comparison scenarios
    const comparisonScenarios = [
      {
        name: t('lessSpots'),
        weeklySpots: Math.round(weeklySpots * 0.7),
        weeks: weeks,
        reach: Math.round(estimatedReach * 0.85),
        frequency: parseFloat(estimatedFrequency) * 0.7,
        grps: Math.round(totalGRPs * 0.6),
        efficiency: t('lower')
      },
      {
        name: t('currentStrategy'),
        weeklySpots: weeklySpots,
        weeks: weeks,
        reach: estimatedReach,
        frequency: parseFloat(estimatedFrequency),
        grps: totalGRPs,
        efficiency: t('optimal')
      },
      {
        name: t('shorterDuration'),
        weeklySpots: weeklySpots,
        weeks: Math.max(2, weeks - 2),
        reach: Math.round(estimatedReach * 0.9),
        frequency: parseFloat(estimatedFrequency) * 0.8,
        grps: Math.round(totalGRPs * 0.7),
        efficiency: t('medium')
      }
    ];

    setResults({
      weeklyGRPs,
      totalGRPs,
      estimatedReach,
      estimatedFrequency,
      weeks,
      weeklySpots,
      totalSpots,
      weeklyData,
      dailyBreakdown,
      comparisonScenarios
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Helper function to generate recommendations
  const generateRecommendations = () => {
    if (!results) return [];
    
    const freq = parseFloat(results.estimatedFrequency);
    const grps = results.totalGRPs;
    const recommendations = [];

    // Check Frequency
    if (freq < 3) {
      const targetFreq = 4;
      const neededSpots = Math.ceil((targetFreq * results.estimatedReach * results.weeks) - results.totalSpots);
      const newWeeklySpots = Math.ceil(results.weeklySpots + (neededSpots / results.weeks));
      const newGRPs = Math.round(results.estimatedReach * targetFreq);
      const extraSpots = newWeeklySpots - results.weeklySpots;
      
      recommendations.push({
        type: 'critical',
        icon: '🚨',
        title: t('criticalLowFreq'),
        message: t('criticalLowFreqMsgDynamic').replace('{freq}', results.estimatedFrequency),
        suggestion: t('recIncreaseSpotsTo')
          .replace('{newWeeklySpots}', newWeeklySpots)
          .replace('{extraSpots}', extraSpots)
          .replace('{targetFreq}', targetFreq)
          .replace('{newGRPs}', newGRPs)
      });
    } else if (freq >= 10) {
      const targetFreq = 7;
      const reduceSpots = Math.floor(results.totalSpots - (targetFreq * results.estimatedReach * results.weeks));
      const newWeeklySpots = Math.floor(results.weeklySpots - (reduceSpots / results.weeks));
      
      recommendations.push({
        type: 'warning',
        icon: '⚠️',
        title: t('warningHighFreq'),
        message: t('warningHighFreqMsgDynamic').replace('{freq}', results.estimatedFrequency),
        suggestion: t('recReduceSpotsTo')
          .replace('{newWeeklySpots}', newWeeklySpots)
          .replace('{targetFreq}', targetFreq)
      });
    }

    // Check GRPs
    if (grps < 400) {
      const option1_weeks = results.weeks + 2;
      const option1_newGRPs = Math.round((results.totalGRPs / results.weeks) * option1_weeks);
      const option2_spotsIncrease = 15;
      const option2_newWeeklySpots = results.weeklySpots + option2_spotsIncrease;
      const option2_newFreq = ((option2_newWeeklySpots * results.weeks) / (results.estimatedReach * results.weeks)).toFixed(1);
      const option2_newGRPs = Math.round(results.estimatedReach * option2_newFreq);
      
      recommendations.push({
        type: 'suggestion',
        icon: '💡',
        title: t('suggestionIncrease'),
        message: t('suggestionIncreaseMsgDynamic').replace('{grps}', grps),
        suggestion: t('recOptionsAB')
          .replace('{option1_weeks}', option1_weeks)
          .replace('{option1_newGRPs}', option1_newGRPs)
          .replace('{option2_spotsIncrease}', option2_spotsIncrease)
          .replace('{option2_newWeeklySpots}', option2_newWeeklySpots)
          .replace('{option2_newGRPs}', option2_newGRPs)
      });
    } else if (grps >= 800) {
      recommendations.push({
        type: 'info',
        icon: '💎',
        title: t('excellentGRPs'),
        message: t('excellentGRPsMsgDynamic').replace('{grps}', grps),
        suggestion: t('recWatchSaturation')
      });
    } else if (grps >= 400 && grps < 600) {
      recommendations.push({
        type: 'success',
        icon: '✅',
        title: t('optimalCampaign'),
        message: t('optimalCampaignMsgDynamic').replace('{grps}', grps).replace('{freq}', results.estimatedFrequency),
        suggestion: t('recWellDesigned')
      });
    } else if (grps >= 600 && grps < 800) {
      recommendations.push({
        type: 'success',
        icon: '🎯',
        title: t('strongCampaign'),
        message: t('strongCampaignMsgDynamic').replace('{grps}', grps),
        suggestion: t('recExcellentStrategy')
      });
    }

    // If everything is optimal
    if (freq >= 3 && freq <= 7 && grps >= 400 && grps < 800 && recommendations.length === 0) {
      recommendations.push({
        type: 'success',
        icon: '🌟',
        title: t('perfectCampaign'),
        message: t('perfectCampaignMsg'),
        suggestion: t('recBestBalance')
      });
    }

    return recommendations;
  };

  const exportToPDF = async () => {
    if (!results) {
      toast.error('⚠️ ' + t('calculateFirst'));
      return;
    }
    
    // Show loading toast
    const loadingToast = toast.loading('⏳ ' + t('waitForPDFGen'));
    
    try {
      // Update button to show loading
      const exportButton = document.activeElement;
      if (exportButton && exportButton.tagName === 'BUTTON') {
        exportButton.disabled = true;
        exportButton.textContent = '⏳ ' + t('pleaseWait');
      }
      
      // Generate recommendations before export
      const recommendations = generateRecommendations();
      
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/campaign/export/pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          formData,
          results,
          dailySpots,
          recommendations
        })
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Campaign_Analysis_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      // Re-enable button
      if (exportButton && exportButton.tagName === 'BUTTON') {
        exportButton.disabled = false;
        exportButton.textContent = '📄 ' + t('exportToPDF');
      }
      
      toast.dismiss(loadingToast);
      toast.success('✅ ' + t('exportCompleted') + '\n\n' + t('passwordToOpen'), {
        duration: 8000
      });
    } catch (error) {
      console.error('Export to PDF failed:', error);
      
      // Re-enable button on error
      const exportButton = document.activeElement;
      if (exportButton && exportButton.tagName === 'BUTTON') {
        exportButton.disabled = false;
        exportButton.textContent = '📄 ' + t('exportToPDF');
      }
      
      toast.dismiss(loadingToast);
      toast.error('❌ ' + (error.message || t('exportError')));
    }
  };

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-[#F8F6FB] via-[#F5F0FA] to-[#F0EBF8] dark:from-[#1A0730] dark:via-[#2D0C4B] dark:to-[#1A0730]">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{SEO_CONFIG.title}</title>
        <meta name="description" content={SEO_CONFIG.description} />
        <meta name="keywords" content={SEO_CONFIG.keywords} />
        <meta property="og:title" content={SEO_CONFIG.title} />
        <meta property="og:description" content={SEO_CONFIG.description} />
        <meta property="og:url" content={SEO_CONFIG.ogUrl} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={SEO_CONFIG.ogUrl} />
      </Helmet>
      
      {/* Unified Header */}
      <ToolPageHeader title={t('campaignAnalyzerPageTitle')} />
      
      <div className="px-4 py-8 flex justify-center">
        <div style={{ maxWidth: '700px', width: '100%' }}>
          <Card className="shadow-lg border-2 border-[#8A4FDB]/30" style={{ width: '100%', maxWidth: '700px' }}>
        <CardHeader className="bg-gradient-to-r from-[#4A1D7A] to-[#6B3FA0] text-white">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-3xl">📊 {t('campaignAnalyzerPageTitle')}</CardTitle>
              <CardDescription className="text-[#E9D5FF]">
                {t('campaignAnalyzerPageDesc')}
              </CardDescription>
            </div>
            <img src="/assets/logo_main.png" alt="Radio Solutions" className="h-12 w-auto" />
          </div>
          <div className="mt-2 text-center">
            <span className="text-sm font-semibold text-[#C4B5FD]">© Radio Solutions</span>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 gap-4 mb-6" style={{ maxWidth: '100%' }}>
            {/* Brand Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                {t('brandCategoryLabel')}
              </label>
              <select
                value={formData.brandCategory}
                onChange={(e) => handleInputChange('brandCategory', e.target.value)}
                className="w-full px-4 py-3 border border-[#8A4FDB]/30 rounded-lg focus:ring-2 focus:ring-[#6B3FA0] focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">{t('pleaseSelect')}</option>
                {brandCategories.map(cat => (
                  <option key={cat.value} value={cat.value}>{t(cat.labelKey)}</option>
                ))}
              </select>
            </div>

            {/* Market Position */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                {t('marketPositionLabel')}
              </label>
              <select
                value={formData.marketPosition}
                onChange={(e) => handleInputChange('marketPosition', e.target.value)}
                className="w-full px-4 py-3 border border-[#8A4FDB]/30 rounded-lg focus:ring-2 focus:ring-[#6B3FA0] focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">{t('pleaseSelect')}</option>
                {marketPositions.map(pos => (
                  <option key={pos.value} value={pos.value}>{t(pos.labelKey)}</option>
                ))}
              </select>
            </div>

            {/* Radio Primary */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                {t('radioPrimaryLabel')}
              </label>
              <select
                value={formData.radioPrimary}
                onChange={(e) => handleInputChange('radioPrimary', e.target.value)}
                className="w-full px-4 py-3 border border-[#8A4FDB]/30 rounded-lg focus:ring-2 focus:ring-[#6B3FA0] focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">{t('pleaseSelect')}</option>
                {radioPrimaryOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                ))}
              </select>
            </div>

            {/* Campaign Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                {t('campaignDurationLabel')}
              </label>
              <select
                value={formData.campaignDuration}
                onChange={(e) => handleInputChange('campaignDuration', e.target.value)}
                className="w-full px-4 py-3 border border-[#8A4FDB]/30 rounded-lg focus:ring-2 focus:ring-[#6B3FA0] focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">{t('pleaseSelect')}</option>
                {campaignDurations.map(dur => (
                  <option key={dur.value} value={dur.value}>{t(dur.labelKey)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Daily Spots Section */}
          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-3">📅 {t('spotsPerDay')}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">{t('adjustSpotsPerDay')}</p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {weekDays.map(day => (
                <div key={day.key}>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                    {t(day.labelKey)}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    value={dailySpots[day.key]}
                    onChange={(e) => setDailySpots(prev => ({
                      ...prev,
                      [day.key]: parseInt(e.target.value) || 0
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              ))}
            </div>
            
            <div className="mt-3 p-2 bg-white dark:bg-gray-800 rounded border border-blue-300 dark:border-blue-600">
              <p className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                {t('totalSpotsWeek')}: {Object.values(dailySpots).reduce((sum, spots) => sum + (parseInt(spots) || 0), 0)}
              </p>
            </div>
          </div>

          <Button 
            onClick={calculateCampaign}
            className="w-full bg-gradient-to-r from-[#4A1D7A] to-[#6B3FA0] hover:from-[#5E2D9E] hover:to-[#8A4FDB] text-white font-semibold py-3"
          >
            {t('calculateCampaign')}
          </Button>

          {/* Results Section */}
          {results && (
            <div className="mt-8 space-y-6">
              {/* Summary Cards */}
              <div className="grid md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/50 dark:to-blue-800/50">
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-300">{t('weeklyGRPs')}</p>
                    <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{results.weeklyGRPs}</p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/50 dark:to-green-800/50">
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-300">{t('totalGRPs')}</p>
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">{results.totalGRPs}</p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/50 dark:to-purple-800/50">
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-300">{t('estimatedReach')}</p>
                    <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{results.estimatedReach}%</p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-pink-50 to-pink-100 dark:from-pink-900/50 dark:to-pink-800/50">
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-300">{t('avgFrequency')}</p>
                    <p className="text-3xl font-bold text-pink-600 dark:text-pink-400">{results.estimatedFrequency}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Export Button */}
              <div className="flex flex-col items-center gap-2">
                <Button
                  onClick={() => exportToPDF()}
                  className="bg-[#4A1D7A] hover:bg-[#6B3FA0] text-white px-8 py-4 flex items-center gap-3 text-lg font-bold shadow-lg hover:shadow-xl"
                  title={t('exportPDFBtn')}
                >
                  <span>📄</span>
                  {t('exportPDFBtn')}
                </Button>
                <p className="text-xs text-gray-500 italic text-center">
                  ⏳ {t('waitForPDF')}
                </p>
                <p className="text-sm text-gray-600 italic">
                  🔐 {t('pdfPassword')}
                </p>
              </div>

              {/* Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('weeklyProgress')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={results.weeklyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="GRPs" fill="#8b5cf6" name="GRPs" />
                      <Bar dataKey="Reach" fill="#ec4899" name="Reach %" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Daily Breakdown Table */}
              <Card>
                <CardHeader>
                  <CardTitle>📊 {t('dailyDistribution')}</CardTitle>
                  <CardDescription>{t('adjustSpotsPerDay')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-2 text-left dark:text-gray-200">{t('monday').split(' ')[0] === t('monday') ? 'Day' : 'Ημέρα'}</th>
                          <th className="px-4 py-2 text-center dark:text-gray-200">Spots</th>
                          <th className="px-4 py-2 text-center dark:text-gray-200">{t('all')} ({results.weeks} {t('weekLabel').toLowerCase()})</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.dailyBreakdown.map((day, idx) => (
                          <tr key={idx} className="border-b dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-4 py-2 font-medium dark:text-gray-200">{day.day}</td>
                            <td className="px-4 py-2 text-center dark:text-gray-300">{day.spots}</td>
                            <td className="px-4 py-2 text-center font-semibold text-blue-600 dark:text-blue-400">
                              {day.spots * results.weeks}
                            </td>
                          </tr>
                        ))}
                        <tr className="bg-blue-50 dark:bg-blue-900/50 font-bold">
                          <td className="px-4 py-2 dark:text-gray-200">{t('all').toUpperCase()}</td>
                          <td className="px-4 py-2 text-center dark:text-gray-200">{results.weeklySpots}</td>
                          <td className="px-4 py-2 text-center text-blue-700 dark:text-blue-400">{results.totalSpots}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                    <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">
                      📐 GRPs = Reach × Frequency = {results.estimatedReach}% × {results.estimatedFrequency} = {results.totalGRPs}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Performance Comparison */}
              <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/40 dark:to-purple-900/40">
                <CardHeader>
                  <CardTitle className="text-indigo-700 dark:text-indigo-400">📈 {t('scenarioComparison')}</CardTitle>
                  <CardDescription>{t('scenarioComparison')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-indigo-100 dark:bg-indigo-900/50">
                        <tr>
                          <th className="px-3 py-2 text-left dark:text-indigo-300">{t('scenario')}</th>
                          <th className="px-3 py-2 text-center dark:text-indigo-300">Spots/{t('weekLabel')}</th>
                          <th className="px-3 py-2 text-center dark:text-indigo-300">{t('weekLabel')}</th>
                          <th className="px-3 py-2 text-center dark:text-indigo-300">Reach%</th>
                          <th className="px-3 py-2 text-center dark:text-indigo-300">Frequency</th>
                          <th className="px-3 py-2 text-center dark:text-indigo-300">GRPs</th>
                          <th className="px-3 py-2 text-center dark:text-indigo-300">{t('efficiency')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.comparisonScenarios.map((scenario, idx) => (
                          <tr 
                            key={idx} 
                            className={`border-b dark:border-gray-600 ${scenario.name === t('currentStrategy') ? 'bg-green-50 dark:bg-green-900/30 font-bold' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                          >
                            <td className="px-3 py-2 dark:text-gray-200">
                              {scenario.name === t('currentStrategy') && '✅ '}
                              {scenario.name}
                            </td>
                            <td className="px-3 py-2 text-center dark:text-gray-300">{scenario.weeklySpots}</td>
                            <td className="px-3 py-2 text-center dark:text-gray-300">{scenario.weeks}</td>
                            <td className="px-3 py-2 text-center dark:text-gray-300">{scenario.reach}%</td>
                            <td className="px-3 py-2 text-center dark:text-gray-300">{scenario.frequency.toFixed(1)}</td>
                            <td className="px-3 py-2 text-center font-semibold text-indigo-600 dark:text-indigo-400">
                              {scenario.grps}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <span className={`px-2 py-1 rounded text-xs ${
                                scenario.efficiency === t('optimal') ? 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200' :
                                scenario.efficiency === t('medium') ? 'bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200' :
                                'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200'
                              }`}>
                                {scenario.efficiency}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="mt-4 space-y-2">
                    <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-indigo-200 dark:border-indigo-700">
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        <strong className="text-indigo-700 dark:text-indigo-400">💡 Insight:</strong> {results.weeklySpots} spots/{t('weekLabel').toLowerCase()} × {results.weeks} {t('weekLabel').toLowerCase()} 
                        = {((results.totalGRPs / results.comparisonScenarios[0].grps - 1) * 100).toFixed(0)}% more GRPs
                      </p>
                    </div>
                    <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-indigo-200 dark:border-indigo-700">
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        <strong className="text-indigo-700 dark:text-indigo-400">⚠️ Note:</strong> {t('shorterDuration')} = -{100 - Math.round((results.comparisonScenarios[2].reach / results.estimatedReach) * 100)}% reach
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Frequency & GRPs Guidelines */}
              <Card className="bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/40 dark:to-cyan-900/40 border-2 border-teal-300 dark:border-teal-700">
                <CardHeader>
                  <CardTitle className="text-teal-800 dark:text-teal-300">📘 {t('helpGuide')}</CardTitle>
                  <CardDescription>{t('howItWorks')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Frequency Levels */}
                  <div>
                    <h4 className="font-bold text-teal-700 dark:text-teal-400 mb-3 text-lg">🎯 {t('avgFrequency')}</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/30 rounded border-l-4 border-red-500">
                        <span className="text-xl">❌</span>
                        <span className="text-sm dark:text-gray-200"><strong>0-1:</strong> {t('freqInsufficient')}</span>
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-orange-50 dark:bg-orange-900/30 rounded border-l-4 border-orange-400">
                        <span className="text-xl">⚠️</span>
                        <span className="text-sm dark:text-gray-200"><strong>1-2:</strong> {t('freqVeryLow')}</span>
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/30 rounded border-l-4 border-green-500">
                        <span className="text-xl">✅</span>
                        <span className="text-sm dark:text-gray-200"><strong>3-5:</strong> {t('freqIdeal')}</span>
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/30 rounded border-l-4 border-green-600">
                        <span className="text-xl">✅</span>
                        <span className="text-sm dark:text-gray-200"><strong>5-7:</strong> {t('freqVeryGood')}</span>
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-yellow-50 dark:bg-yellow-900/30 rounded border-l-4 border-yellow-500">
                        <span className="text-xl">⚠️</span>
                        <span className="text-sm dark:text-gray-200"><strong>7-10:</strong> {t('freqHigh')}</span>
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/30 rounded border-l-4 border-red-500">
                        <span className="text-xl">❌</span>
                        <span className="text-sm dark:text-gray-200"><strong>10+:</strong> {t('freqTooHigh')}</span>
                      </div>
                    </div>
                    <div className="mt-3 p-3 bg-teal-100 dark:bg-teal-900/40 rounded-lg">
                      <p className="text-sm text-teal-800 dark:text-teal-300">
                        <strong>{t('yourCampaign')}:</strong> Frequency <strong>{results.estimatedFrequency}</strong> - 
                        {parseFloat(results.estimatedFrequency) < 1 ? ` ❌ ${t('freqInsufficient')}` :
                         parseFloat(results.estimatedFrequency) < 3 ? ` ⚠️ ${t('freqVeryLow')}` :
                         parseFloat(results.estimatedFrequency) < 5 ? ` ✅ ${t('freqIdeal')}` :
                         parseFloat(results.estimatedFrequency) < 7 ? ` ✅ ${t('freqVeryGood')}` :
                         parseFloat(results.estimatedFrequency) < 10 ? ` ⚠️ ${t('freqHigh')}` :
                         ` ❌ ${t('freqTooHigh')}`}
                      </p>
                    </div>
                  </div>

                  {/* GRPs Levels */}
                  <div>
                    <h4 className="font-bold text-teal-700 dark:text-teal-400 mb-3 text-lg">📊 {t('grpLevels')}</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/30 rounded border-l-4 border-red-500">
                        <span className="text-xl">❌</span>
                        <span className="text-sm dark:text-gray-200"><strong>0-100:</strong> {t('grpVeryLow')}</span>
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-orange-50 dark:bg-orange-900/30 rounded border-l-4 border-orange-400">
                        <span className="text-xl">⚠️</span>
                        <span className="text-sm dark:text-gray-200"><strong>100-200:</strong> {t('grpLow')}</span>
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-yellow-50 dark:bg-yellow-900/30 rounded border-l-4 border-yellow-500">
                        <span className="text-xl">⚠️</span>
                        <span className="text-sm dark:text-gray-200"><strong>200-400:</strong> {t('grpGood')}</span>
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/30 rounded border-l-4 border-green-500">
                        <span className="text-xl">✅</span>
                        <span className="text-sm dark:text-gray-200"><strong>400-600:</strong> {t('grpVeryGood')}</span>
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/30 rounded border-l-4 border-green-600">
                        <span className="text-xl">✅</span>
                        <span className="text-sm dark:text-gray-200"><strong>600-800:</strong> {t('grpHigh')}</span>
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-purple-50 dark:bg-purple-900/30 rounded border-l-4 border-purple-500">
                        <span className="text-xl">💎</span>
                        <span className="text-sm dark:text-gray-200"><strong>800+:</strong> {t('grpVeryHigh')}</span>
                      </div>
                    </div>
                    <div className="mt-3 p-3 bg-teal-100 dark:bg-teal-900/40 rounded-lg">
                      <p className="text-sm text-teal-800 dark:text-teal-300">
                        <strong>{t('yourCampaign')}:</strong> {results.totalGRPs} GRPs - 
                        {results.totalGRPs < 100 ? ` ❌ ${t('grpVeryLow')}` :
                         results.totalGRPs < 200 ? ` ⚠️ ${t('grpLow')}` :
                         results.totalGRPs < 400 ? ` ⚠️ ${t('grpGood')}` :
                         results.totalGRPs < 600 ? ` ✅ ${t('grpVeryGood')}` :
                         results.totalGRPs < 800 ? ` ✅ ${t('grpHigh')}` :
                         ` 💎 ${t('grpVeryHigh')}`}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Smart Recommendations */}
              <Card className="bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/40 dark:to-orange-900/40">
                <CardHeader>
                  <CardTitle className="text-orange-700 dark:text-orange-300">💡 {t('recommendations')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Current Campaign Summary */}
                  <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-orange-200 dark:border-orange-700">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">📊 {t('yourCampaign')}:</h4>
                    <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                      <li>• {t('totalSpots')}: <strong>{results.totalSpots}</strong> ({results.weeklySpots} spots/{t('weekLabel').toLowerCase()} × {results.weeks} {t('weekLabel').toLowerCase()})</li>
                      <li>• Reach: <strong>{results.estimatedReach}%</strong> | Frequency: <strong>{results.estimatedFrequency}</strong></li>
                      <li>• {t('totalGRPs')}: <strong>{results.totalGRPs}</strong></li>
                    </ul>
                  </div>

                  {/* Smart Recommendations Based on Performance */}
                  <div className="space-y-3">
                    {generateRecommendations().map((rec, idx) => (
                      <div 
                        key={idx}
                        className={`p-4 rounded-lg border-2 ${
                          rec.type === 'critical' ? 'bg-red-50 dark:bg-red-900/30 border-red-300 dark:border-red-700' :
                          rec.type === 'warning' ? 'bg-orange-50 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700' :
                          rec.type === 'suggestion' ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700' :
                          rec.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 border-green-300 dark:border-green-700' :
                          'bg-purple-50 dark:bg-purple-900/30 border-purple-300 dark:border-purple-700'
                        }`}
                      >
                        <h4 className="font-bold text-gray-800 dark:text-gray-200 mb-2 flex items-center gap-2">
                          <span className="text-2xl">{rec.icon}</span>
                          {rec.title}
                        </h4>
                        <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{rec.message}</p>
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 whitespace-pre-line">
                          {rec.suggestion}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>
      </div>
      </div>
    </div>
  );
};

export default CampaignCalculator;
